import { UnderlineText } from "components/atoms/text/Text";
import * as s from "../../auth/signup/SignupStyled";
import { useAuth } from "components/Auth";

interface Props {}

export function EULAModal(props: Props): JSX.Element {
  const { user } = useAuth();
  return (
    <s.EULAText>
      <b>MYDW SOFTWARE END USER LICENSE AGREEMENT</b>
      <br />
      <br />
      <b>IMPORTANT - PLEASE READ CAREFULLY:</b> This End User License Agreement
      (this “EULA”) is a legally enforceable contract between You (either an
      individual or an entity) and Kaltec Electronics, Inc. (d/b/a Digital
      Watchdog, the “Company”) regarding the use of the Company’s software
      programs accompanying or provided under this EULA together with the
      software development kit made available by the Company, associated media,
      corresponding Documentation, technical configurations, and technical data
      (collectively, the “Software”). “Documentation” means the user’s guide and
      other documentation (including print and online), if any, provided to you
      with the Software.
      <br />
      <br />
      This EULA governs your use of the Software and is subject to and
      incorporates by reference the terms of any separate Software as a Service
      Subscription Agreement ("SaaS Agreement") between you and the Company. In
      the event of any conflict between this EULA and the SaaS Agreement, the
      SaaS Agreement shall control with respect to subscription terms, service
      levels, fees, billing, data processing and termination provisions.
      <br />
      <br />
      <b>
        BY CLICKING THE “I ACCEPT” BUTTON BELOW OR BY DOWNLOADING, INSTALLING OR
        OTHERWISE USING THE SOFTWARE, YOU CONSENT TO BE LEGALLY BOUND BY ALL THE
        TERMS AND CONDITIONS OF THIS AGREEMENT AND ANY APPLICABLE SAAS
        AGREEMENT. IF YOU ARE ENTERING INTO THIS AGREEMENT ON BEHALF OF A
        COMPANY OR OTHER LEGAL ENTITY, YOU REPRESENT THAT YOU HAVE THE AUTHORITY
        TO BIND SUCH ENTITY AND ITS AFFILIATES TO THIS AGREEMENT, IN WHICH CASE
        THE TERMS “YOU” OR “YOUR” SHALL REFER TO SUCH ENTITY AND ITS AFFILIATES.
        IF YOU DO NOT HAVE SUCH AUTHORITY, OR IF YOU DO NOT AGREE WITH ANY OF
        THE TERMS OF THIS AGREEMENT OR THE SAAS AGREEMENT, YOU MUST NOT ACCEPT
        THIS AGREEMENT AND MAY NOT USE THE SOFTWARE.
      </b>
      <br />
      <br />
      In addition, certain third party programs or code (“Third Party Software”)
      are being provided to you under the terms and conditions of a separate end
      user license accompanying such Third Party Software (“Third Party Software
      Licenses”), which will be provided to you by the Company as part of the
      Documentation or may be made available for viewing by the Company at the
      website or webpage
      {user.userId && (
        <>
          at{" "}
          <UnderlineText
            className="button"
            style={{ display: "inline" }}
            onClick={() => window.open("https://www.mydw.cloud/help/about")}
            data-testid="terms-text-text-underline-text-open"
          >
            https://www.mydw.cloud/help/about
          </UnderlineText>{" "}
          (the “Third Party Site”)
        </>
      )}
      . If required by any Third Party Software License, the Company will make
      such Third Party Software, and the Company’s modifications to that Third
      Party Software, available to you on the Third Party Site. The Company may
      be required or wish to provide you with certain notices or information
      about Third Party Software or Third Party Software Licenses and you
      acknowledge that such notices and information may be provided to you by
      the posting of such notices and information on the Third Party Site. The
      Company reserves the right to update the Third Party Site at any time
      without notice.{" "}
      <b>
        BY INSTALLING, COPYING OR OTHERWISE USING THE SOFTWARE, YOU AGREE TO BE
        BOUND BY THE TERMS OF THIS AGREEMENT AS WELL AS THE TERMS OF THIRD PARTY
        SOFTWARE LICENSES AND ANY APPLICABLE SAAS AGREEMENT. IF YOU DO NOT AGREE
        TO ALL SUCH TERMS, DO NOT INSTALL OR USE THE SOFTWARE.
      </b>
      <ol>
        <li>
          <b>
            <span className="number">1</span> Definitions.
          </b>{" "}
          For purposes of this EULA:
          <ul>
            <li>
              <b>"SaaS Services"</b> means the hosted software services provided
              by the Company under a separate SaaS Agreement, including but not
              limited to cloud-based storage, processing, analytics, monitoring,
              and remote access capabilities.
            </li>
            <li>
              <b>"Subscription Term"</b> means the period during which you have
              a valid, paid subscription to the SaaS Services as defined in the
              applicable SaaS Agreement.{" "}
            </li>
            <li>
              <b>"Authorized Users"</b> means the number of individual users
              permitted to access the Software and SaaS Services as specified in
              your current SaaS Agreement.{" "}
            </li>
            <li>
              <b>"Customer Data"</b> means data uploaded, stored, or processed
              through the Software and SaaS Services.{" "}
            </li>
          </ul>
        </li>
        <li>
          <b>
            <span className="number">2</span> License Grant
          </b>
          <ol>
            <li>
              <b>
                <span className="number">2.1</span> Subscription Based License.
              </b>{" "}
              Subject to the terms and conditions of this EULA and any
              applicable SAAS Agreement, the Company grants you a revocable,
              non-exclusive, non-transferable, non-sublicensable, limited
              license to install and use the Software during an active
              Subscription Term, in object code version only, for your internal
              business use only, subject to the restrictions contained in this
              EULA.
            </li>
            <li>
              <b>
                <span className="number">2.2</span> License Dependency.
              </b>{" "}
              Your license to use the Software is contingent upon maintaining an
              active subscription to the Company’s SaaS Services. Certain
              features of the Software may not function without a valid
              subscription, and the Company may disable Software functionality
              upon subscription expiration.
            </li>
            <li>
              <b>
                <span className="number">2.3</span> User and Device Limitations.
              </b>{" "}
              Use of the Software is strictly limited to the number of
              Authorized Users and devices specified in your SaaS Agreement. You
              may not exceed these limits without upgrading your subscription.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <span className="number">3</span> Backup Copies and Data Collection.
          </b>{" "}
          You may make one backup copy of the Software, provided your backup
          copy is not installed or used on any computer and further provided
          that all such copies shall bear the original and unmodified copyright,
          patent and other intellectual property markings that appear on or in
          the Software. You may not transfer the rights to a backup copy unless
          you transfer all rights in the Software as provided in this EULA. You
          acknowledge and agree that the Software and SaaS Services may collect
          and transmit usage data, performance metrics, system information,
          feature utilization analytics and operational data to the Company for
          product improvement license compliance verification, service
          optimization, security monitoring and business analytics purposes.
          Such data collection may occur automatically and continuously during
          your use of the Software. This may include personally identifiable
          information, as described in the Company’s Privacy Policy, which is
          available at the Company’s website.
        </li>
        <li>
          <b>
            <span className="number">4</span> Ownership and Intellectual
            Property.
          </b>{" "}
          You acknowledge that the Software, SaaS Services and any copies that
          you are authorized by the Company to make are the intellectual
          property of, and are owned by, the Company and its licensors. The
          structure, sequence and organization of the Software are valuable
          trade secrets and confidential information of the Company and its
          licensors. The Software is protected by copyright, including without
          limitation by United States copyright law, international treaty
          provisions and applicable laws in the country in which it is used. You
          acknowledge and agree that the Company, or its licensors, retain the
          ownership of all patents, copyrights, trade secrets, trademarks and
          other intellectual property rights pertaining to the Software. You
          will take no actions which adversely affect the Company’s intellectual
          property rights in the Software. This EULA is a license and not an
          agreement for sale. No title to, or ownership of, the Software, or any
          intellectual property rights subsisting therein, is transferred to
          you. You agree that any feedback, suggestions, improvements, or
          derivative works you create based on the Software shall become the
          exclusive property of the Company without compensation. This includes
          but is not limited to user interface modifications, workflow
          configurations, integration methods, and enhancement recommendations.
        </li>
        <li>
          <b>
            <span className="number">5</span> Restrictions on Use.
          </b>{" "}
          The Software is licensed solely for your own internal business use
          during an active Subscription Term. The Company and its licensors
          reserve all rights not expressly granted to you in this EULA. Without
          limiting the generality of the foregoing, you shall not nor shall you
          permit any other party to: (i) make copies of the Software except as
          expressly set forth in this EULA, (ii) disassemble, decompile, reverse
          engineer or translate any part of the Software, or otherwise attempt
          to reconstruct or discover the source code of the Software; (iii)
          modify or create derivative works based upon the Software; (iv)
          externally distribute, sublicense, resell, encumber or otherwise
          transfer the Software; (v) rent, lease, lend, or use the Software, or
          any part thereof, for timesharing, service bureau or commercial
          hosting purposes; (vi) allow unauthorized third parties to copy,
          access, or use the Software or SaaS Services; (vii) alter or remove
          any copyright, trademark or other proprietary notice which may appear
          on the Software or in connection with the SaaS Services; (viii) take
          any action that would cause the Software to be placed in the public
          domain or become subject to an open source license agreement; (ix) use
          the Software to develop competing products or services; (x) exceed the
          user, device, storage, or usage limits specified in your SaaS
          Agreement; (xi) share, transfer, or misuse login credentials for SaaS
          Services; (xii) attempt to disrupt, interfere with, or compromise the
          security or availability of SaaS Services; (xiii) benchmark, test or
          evaluate the Software for competitive purposes; (xiv) use the Software
          in any manner that could damage the Company’s reputation; (xv)
          circumvent or disable any security features, license verification
          mechanisms, or usage monitoring systems; (xvi) use the Software after
          subscription expiration or during any suspension period; or (xvii) use
          the Software in any manner that violates any statute, law, rule,
          regulation, directive, guideline, bylaw whether presently in force or
          may be implemented by federal, state or local authorities.
        </li>
        <li>
          <b>
            <span className="number">6</span> Subscription Based Features and
            Billing.
          </b>
          <ol>
            <li>
              <b>
                <span className="number">6.1</span> Feature Access.
              </b>{" "}
              The software may provide access to different feature sets based on
              your subscription tier as defined in the SaaS Agreement. Features
              may be enabled, disabled or modified based on your current
              subscription status and payment history
            </li>
            <li>
              <b>
                <span className="number">6.2</span> Premium Features.
              </b>{" "}
              The Company may offer certain premium features to you upon payment
              of license fees determined by the Company in its sole discretion
              (“Premium Features”). License fees are non-refundable and subject
              to change as provided in the SaaS Agreement. Failure to pay fees
              when due may result in immediate suspension of access to Premium
              Features and SaaS Services.
            </li>
            <li>
              <b>
                <span className="number">6.3</span> Billing Integration.
              </b>{" "}
              The Software may integrate with the Company’s billing and
              subscription management systems to monitor usage, enforce limits,
              and manage feature access automatically.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <span className="number">7</span> Updates and Maintenance.
          </b>
          <ol>
            <li>
              <b>
                <span className="number">7.1</span> Subscription-Based Updates.
              </b>{" "}
              During an active Subscription Term, you may receive updates and
              upgrades to the Software in accordance with your subscription
              level. The Company may require mandatory updates and reserves the
              right to discontinue support for older versions.
            </li>
            <li>
              <b>
                <span className="number">7.2</span> Automatic Updates.
              </b>{" "}
              The Company may automatically deploy updates to the Software and
              SaaS Services without prior notice. You agree to accept such
              updates as part of your subscription.
            </li>
            <li>
              <b>
                <span className="number">7.3</span> End of Support.
              </b>{" "}
              Upon termination or expiration of your subscription, you will no
              longer receive updates, maintenance, or support for the Software.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <span className="number">8</span> Support and Service Levels
          </b>
          <ol>
            <li>
              <b>
                <span className="number">8.1</span> Support Scope.
              </b>{" "}
              Support, maintenance, and service level commitments are governed
              by the SaaS Agreement. This EULA does not provide any independent
              support obligations.
            </li>
            <li>
              <b>
                <span className="number">8.2</span> No Warranty of Support.
              </b>{" "}
              Except as expressly required by applicable law or as specified in
              a separate SaaS Agreement, nothing in this EULA entitles you to
              any support, maintenance, or new versions of the Software.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <span className="number">9</span> Data Ownership and Processing.
          </b>
          <ol>
            <li>
              <b>
                <span className="number">9.1</span> Customer Data Ownership.
              </b>{" "}
              You retain ownership of Customer Data, subject to the terms of
              this EULA and any applicable SaaS Agreement.
            </li>
            <li>
              <b>
                <span className="number">9.2</span> Data Processing Rights.
              </b>{" "}
              You grant the Company a non-exclusive, worldwide, royalty-free
              license to access, use, process, and analyze Customer Data as
              necessary to provide SaaS Services, including for backup,
              security, optimization, analytics, and operational purposes.
            </li>
            <li>
              <b>
                <span className="number">9.3</span> Data Retention and Deletion.
              </b>{" "}
              Upon termination of your subscription, data retention and deletion
              procedures will be governed by the SaaS Agreement. The Company
              reserves the right to delete Customer Data after termination in
              accordance with its data retention policies.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <span className="number">10</span> Disclaimer of Warranties. THE
            SOFTWARE AND SAAS SERVICES ARE PROVIDED “AS IS,” WITHOUT WARRANTY OR
            REPRESENTATION OF ANY KIND. THE COMPANY AND ITS LICENSORS DISCLAIM
            ALL WARRANTIES, WHETHER EXPRESS, IMPLIED, OR STATUTORY, INCLUDING
            WITHOUT LIMITATION, ANY IMPLIED WARRANTIES OF NON-INFRINGEMENT,
            MERCHANTABILITY AND FITNESS FOR A PARTICULAR PURPOSE. NEITHER THE
            COMPANY NOR ITS LICENSORS REPRESENT OR WARRANT THAT THE SOFTWARE OR
            SAAS SERVICES WILL SATISFY YOUR REQUIREMENTS OR THAT THEY ARE
            WITHOUT DEFECT OR ERROR OR THAT THE OPERATION THEREOF WILL BE
            UNINTERRUPTED OR ERROR FREE. The Company is not responsible for
            problems caused by changes in, or modifications to, the operating
            characteristics of any computer hardware or operating system for
            which the Software, SaaS Services or any upgrade or update is
            procured, nor is the Company responsible for problems which occur as
            a result of the use of the Software or SaaS Services in conjunction
            with software of third parties or with hardware, which does not meet
            the Company’s specific requirements. Some jurisdictions do not allow
            certain disclaimers or limitations of warranties, so some of these
            may not apply to you.
          </b>
        </li>
        <li>
          <b>
            <span className="number">11</span> Limitation of Liability. TO THE
            MAXIMUM EXTENT PERMITTED BY APPLICABLE LAW, EVEN IF A REMEDY FAILS
            ITS ESSENTIAL PURPOSE, IN NO EVENT SHALL THE COMPANY OR ITS
            LICENSORS BE LIABLE FOR ANY SPECIAL, INCIDENTAL, INDIRECT OR
            CONSEQUENTIAL DAMAGES WHATSOEVER (INCLUDING, WITHOUT LIMITATION,
            DAMAGES FOR LOSS OF PROFITS, BUSINESS INTERRUPTION, LOSS OF BUSINESS
            INFORMATION, OR ANY OTHER PECUNIARY LOSS) ARISING OUT OF THE USE OF
            OR INABILITY TO USE THE SOFTWARE, THE SAAS SERVICES OR THE PROVISION
            OF OR FAILURE TO PROVIDE SUPPORT SERVICES, EVEN IF THE COMPANY HAS
            BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IN ANY EVENT, THE
            COMPANY’S ENTIRE LIABILITY TO YOU OR ANY OTHER PARTY FOR LOSS OR
            DAMAGE RESULTING FROM ANY CLAIMS, DEMANDS OR ACTIONS ARISING OUT OF
            THIS AGREEMENT SHALL NOT EXCEED THE FEE ACTUALLY PAID BY YOU TO THE
            COMPANY FOR THE SOFTWARE AND SAAS SERVICES, IF ANY, IN THE TWELVE
            (12) MONTHS PRECEDING THE CLAIM, NOTWITHSTANDING ANY FAILURE OF
            ESSENTIAL PURPOSE OF ANY LIMITED REMEDY.
          </b>
          <br />
          <br />
          Because some states and jurisdictions do not allow the exclusion or
          limitation of liability, the above limitation may not apply to you. In
          such states and jurisdictions, the Company’s liability shall be
          limited to the greatest extent permitted by law.
        </li>
        <li>
          <b>
            <span className="number">12</span> Sole Remedy and Allocation of
            Risk. YOUR SOLE AND EXCLUSIVE REMEDY AND THE COMPANY’S SOLE AND
            EXCLUSIVE LIABILITY ARE SET FORTH IN THIS AGREEMENT AND THE SAAS
            AGREEMENT.
          </b>{" "}
          This EULA and the SaaS Agreement defines a mutually agreed-upon
          allocation of risk.
        </li>
        <li>
          <b>
            <span className="number">13</span> Third Party Software Licenses.
          </b>{" "}
          Third Party Software included with the Software or SaaS Services
          licensed under the terms of Third Party Software Licenses. You
          understand and agree that, although provided to you by the Company
          with the Software or SaaS Services, your use of Third Party Software
          shall be and is governed by such Third Party Software Licenses. You
          understand and acknowledge that Third Party Software is being provided{" "}
          <b>
            “AS IS” WITHOUT ANY WARRANTY, INCLUDING BUT NOT LIMITED TO, THE
            IMPLIED WARRANTIES OF MERCHANTABILITY OR FITNESS FOR A PARTICULAR
            PURPOSE.
          </b>{" "}
          If there is a conflict between this EULA and the SaaS Agreement on the
          one hand, and any Third Party Software License with respect to Third
          Party Software on the other hand, the provisions of such Third Party
          Software License shall prevail.
        </li>
        <li>
          <b>
            <span className="number">14</span> Export Restrictions.
          </b>{" "}
          You agree to comply with all export and re-export restrictions and
          regulations (“Export Restrictions”) imposed by the United States or
          any other country in which you conduct business. Without limiting the
          generality of the foregoing, and regardless of any disclosure made by
          you to the Company regarding an ultimate destination of the Software
          and SaaS Services, you represent and warrant that you understand the
          U.S. law currently prohibits the export or re-export, directly or
          indirectly (including via remote access) of U.S. origin products and
          technology to certain proscribed countries (including Cuba, Iran,
          Sudan, North Korea and Syria and/or any other country that may become
          subject to an embargo by the United States), entities, organizations
          and individuals, without prior authorization from the U.S. Government.
          You will not commit any act or omission that will result in a breach
          of any such Export Restrictions. Your breach of this clause shall
          constitute cause for immediate termination of this EULA. You represent
          that you are not located in, or acting on behalf of any person or
          entity in, any country subject to U.S. trade sanctions or export
          restrictions.
        </li>
        <li>
          <b>
            <span className="number">15</span> High Risk Activities.
          </b>{" "}
          The Software and Saas Services are not designed, manufactured or
          intended for use or resale as on-line control equipment in hazardous
          environments requiring fail-safe performance, such as in the operation
          of nuclear facilities, aircraft navigation or communication systems,
          air traffic control, direct life support machines, or weapons systems,
          in which the failure of the Software could lead directly to death,
          personal injury, or severe physical or environmental damage (“High
          Risk Activities”). Use in High Risk Activities is strictly prohibited
          and you assume all risks. The Company specifically disclaims any
          express or implied warranty of fitness for High Risk Activities.
        </li>
        <li>
          <b>
            <span className="number">16</span> Indemnification.
          </b>{" "}
          You agree to defend, indemnify, and hold harmless the Company, its
          affiliates, officers, directors, shareholders, employees, and agents
          from and against any claims, damages, costs, liabilities, and expenses
          (including reasonable attorneys’ fees) arising out of or related to:
          (a) your use of the Software or SaaS Services; (b) your breach of this
          EULA or any SaaS Agreement; (c) your violation of any rights of
          another party; or (d) any Customer Data transmitted through the
          Software or SaaS Services.
        </li>
        <li>
          <b>
            <span className="number">17</span> Integration with SaaS Agreement.
          </b>
          <ol>
            <li>
              <span className="number">17.1</span> Complementary Agreements.
              This EULA governs use of downloadable Software components, while
              the SaaS Agreement governs access to hosted services. Both
              agreements are necessary for full functionality.
            </li>
            <li>
              <span className="number">17.2</span> Hierarchy of Terms. In case
              of conflict between this EULA and the SaaS Agreement, the SaaS
              Agreement controls for subscription terms, billing, service
              levels, data processing, and termination, while this EULA controls
              for software licensing and intellectual property matters.
            </li>
            <li>
              <span className="number">17.3</span> Modification Rights. The
              Company may modify this EULA at any time by posting revised terms
              on its website. Your continued use of the Software constitutes
              your acceptance of modifications.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <span className="number">18</span> Termination.
          </b>
          <ol>
            <li>
              <span className="number">18.1</span> Automatic Termination. This
              EULA will automatically terminate without notice upon expiration
              or termination of your SaaS Agreement, or upon your failure to
              comply with any material provision of this EULA.
            </li>
            <li>
              <span className="number">18.2</span> Immediate Termination. The
              Company may terminate this EULA immediately without notice for:
              (i) breach of payment obligations; (ii) violation of usage
              restrictions; (iii) security violations; or (iv) any use that
              could harm the Company’s systems or reputation.
            </li>
            <li>
              <span className="number">18.3</span> Effect of Termination. Upon
              termination, you must immediately: (i) cease all use of the
              Software and SaaS Services; (ii) uninstall all Software; (iii)
              destroy all copies of the Software; and (iv) return or destroy all
              Documentation. The sections of this EULA relating to ownership,
              indemnification, liability limitations, and confidentiality shall
              survive termination.
            </li>
          </ol>
        </li>
        <li>
          <b>
            <span className="number">19</span> Miscellaneous.
          </b>
          <ol>
            <li>
              <span className="number">19.1</span> If you are a unit or agency
              of the United States Government, the following applies: The
              Software and SaaS Services are provided with RESTRICTED RIGHTS.
              Use, duplication or disclosure by the Government is subject to
              restrictions as set forth in Subparagraphs (a) through (d) of the
              Commercial Computer-Restricted Rights clause at FAR 52.227-19 when
              applicable, or in Subparagraph 252.227-7013 (c)(1)(ii) of the
              Rights in Technical Data and Computer Software at DFARS, and in
              similar clauses in the NASA FAR Supplement.
              Contractor/manufacturer is Kaltec Electronics, Inc. dba Digital
              Watchdog.
            </li>
            <li>
              <span className="number">19.2</span> This EULA shall be governed
              by the laws of the State of California, without reference to any
              choice of laws principles.
            </li>
            <li>
              <span className="number">19.3</span> If a dispute arises out of or
              relates to this EULA, or the breach thereof, and if the dispute
              cannot be settled through negotiation, the parties agree first to
              try in good faith to settle the dispute by mediation administered
              by the American Arbitration Association under its Commercial
              Mediation Procedures before resorting to arbitration. The mediator
              shall have at least five years of experience in the security and
              monitoring software industry. Any unresolved controversy or claim
              arising out of or relating to this EULA, or the breach thereof,
              shall be settled by binding arbitration administered by the
              American Arbitration Association in accordance with its Commercial
              Arbitration Rules, and judgment on the award rendered by the
              arbitrator(s) may be entered in any court having jurisdiction
              thereof. The arbitrator(s) shall have at least five years of
              experience in the security and monitoring software industry. If
              all parties to the dispute agree, a mediator involved in the
              parties’ mediation may be asked to serve as the arbitrator. The
              arbitrator(s) will have no authority to award punitive,
              consequential, incidental or other damages not measured by the
              prevailing party’s actual damages, except as may be required by
              statute. Each party shall bear its own costs and expenses and an
              equal share of the arbitrators’ and administrative fees of
              arbitration. Except as may be required by law, neither a party nor
              an arbitrator may disclose the existence, content, or results of
              any arbitration hereunder without the prior written consent of all
              parties. The mediation and arbitration shall be held exclusively
              in Los Angeles, California.
              <br />
              <br />
              Notwithstanding the foregoing, the Company may seek injunctive or
              other equitable relief in any court of competent jurisdiction to
              prevent or cure breaches of Sections 4, 5 or 6 this EULA.
              <br />
              <br />
              <b>
                CLASS ACTION AND COLLECTIVE ACTION WAIVER: THE PARTIES EXPRESSLY
                WAIVE ANY RIGHT TO PURSUE CLAIMS ON A CLASS ACTION, COLLECTIVE
                ACTION, OR REPRESENTATIVE ACTION BASIS. ALL DISPUTES MUST BE
                BROUGHT IN THE PARTIES’ INDIVIDUAL CAPACITY, AND NOT AS A
                PLAINTIFF OR CLASS MEMBER IN ANY PURPORTED CLASS ACTION,
                COLLECTIVE ACTION, OR REPRESENTATIVE PROCEEDING. THE
                ARBITRATOR(S) SHALL HAVE NO AUTHORITY TO CONDUCT ANY CLASS
                ARBITRATION, COLLECTIVE ARBITRATION, OR REPRESENTATIVE
                ARBITRATION, NOR SHALL THE ARBITRATOR(S) HAVE ANY AUTHORITY TO
                CONSOLIDATE INDIVIDUAL ARBITRATIONS INTO A SINGLE PROCEEDING OR
                CREATE ANY FORM OF REPRESENTATIVE OR CLASS PROCEEDING. THIS
                CLASS ACTION WAIVER APPLIES TO BOTH ARBITRATION AND ANY COURT
                PROCEEDINGS.
              </b>
            </li>
            <li>
              <span className="number">19.4</span> You may not assign,
              sublicense, or transfer this EULA, the Software, or any rights or
              obligations hereunder without prior written consent of the
              Company. Any such attempted assignment, sublicense, or transfer
              will be null and void. The Company may terminate this EULA in the
              event of any such attempted assignment, sublicense, or transfer.
            </li>
            <li>
              <span className="number">19.5</span> This EULA is in the English
              language only, and will only be provided in the English language,
              which language will be controlling in all respects, and all
              versions hereof in any other language will not be binding on the
              parties hereto. All communications and notices to be made or given
              pursuant to this EULA must be in the English language.
            </li>
            <li>
              <span className="number">19.6</span> This EULA constitutes the
              final and complete understanding between you and the Company with
              respect to the subject matter of this EULA. Any modifications or
              waivers of this EULA must be in writing and signed by both parties
              hereto.
            </li>
            <li>
              <span className="number">19.7</span> If any provision in this EULA
              is invalid or unenforceable or contrary to applicable law, such
              provision shall be construed, limited, or altered, as necessary,
              to eliminate the invalidity or unenforceability or the conflict
              with applicable law, and all other provisions of this EULA shall
              remain in effect.
            </li>
            <li>
              <span className="number">19.8</span> Failure or delay on the part
              of the Company to exercise any right, power, privilege, or remedy
              will not constitute a waiver of, or bar the later exercise of,
              that or any other right, power, privilege, or remedy of the
              Company.
            </li>
            <li>
              <span className="number">19.9</span> No rule of strict
              construction shall apply against or in favor of either party in
              the construction and interpretation of this EULA.
            </li>
          </ol>
        </li>
      </ol>
      <br />
      <br />
      <b>
        By using the Software and the SaaS Services, you acknowledge that you
        have read, understood, and agree to be bound by this EULA and any
        applicable SaaS Agreement.
      </b>
    </s.EULAText>
  );
}
